import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CardList from "../ui/CardList.jsx";
import FullScreenPanel from "../ui/FullScreenPanel.jsx";
import PdfButton from "../ui/PdfButton.jsx";
import { phoneUi } from "../ui/phoneUi";
import {
  TRACKING_STATUS_OPTIONS,
  filterLots,
  inboundDateLabel,
  isInStock,
  lastDays,
  loadCustody,
  loadInboundOrders,
  loadMaterial,
  loadMaterialList,
  loadMaterialLots,
  loadMaterialTransactions,
  loadTrackingLots,
  materialFigures,
  materialListCandidates,
  onlyOpenLotId,
  openCustodyRows,
  ownerName,
  TRACKING_LIMIT,
  transferDestinations,
  transferFilterDefaults,
} from "../../features/materials/materialData";
import { TRANSACTION_REASONS, addMaterialTransaction, recordMaterialDefect, transferStock } from "../../features/materials/materialActions";
import { loadLotDetail, loadLotNotes, loadLotUsage, lotStatusLabel } from "../../features/materials/lots";
import { loadCustomers } from "../../features/customers/customerData";
import { isTransferableMtn, mtnRemaining } from "../../utils/mtnLot";
import { parseLotNumber } from "../../utils/beacons";
import { useBeaconsByLot } from "../../features/materials/lotBeacons";
import { useFeatures } from "../../utils/features";
import FindProgress from "../../components/FindProgress";
import { LotBeaconCard } from "./materials/BeaconFind.jsx";
import { splitLotUsage } from "../../utils/remnantTracking";
import { stamp } from "../../utils/tmDefects";
import "../mobile.css";

// The phone Materials page (plan Phase 2): search the materials; one material (?id=N) with on hand,
// available, inbound orders, its lots, recent transactions and what sits at vendors; adjust the quantity,
// record defective sheets and transfer stock (the desktop's own actions, each behind the PIN). Lot lookup
// (?tab=tracking, the desktop's Material Tracking tab) by TRK / heat / PO / supplier, then one lot
// (?mtn=N, where a beacon's or label's QR lands) with its heat, usage and notes. Stock-take, remnants,
// intake / returns, RFQs, CWT pricing, the admin tabs, charts and create / edit stay on the desktop.
// Pallet beacons (behind pallet_beacons): a lot's beacon with Find on the lot panel, a Find on each of a
// material's lots that has a beacon, and the beacon's ID on the lot lookup's cards.
// Layout only: features/materials/ is the desktop page's own code.

const LOT_PAGE = 60;
const trk = (id) => String(id).padStart(6, "0");
const positiveId = (raw) => (raw && /^\d+$/.test(raw) && Number(raw) > 0 ? Number(raw) : null);

export default function MaterialsScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = positiveId(searchParams.get("id"));
  const mtn = positiveId(searchParams.get("mtn"));
  const lotsTab = searchParams.get("tab") === "tracking";
  const [customers, setCustomers] = useState([]);
  // Kept here so going back from one material finds the list as it was left (search, Sheet / Shape, rows).
  const [listState, setListState] = useState({ category: "Sheet Stock", search: "", inStockOnly: true, materials: null });
  const [lotState, setLotState] = useState({ status: "on_hand", search: "" });

  useEffect(() => {
    let live = true;
    loadCustomers().then((cs) => { if (live) setCustomers(cs); }).catch(() => {});
    return () => { live = false; };
  }, []);

  const closeLot = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("mtn");
    setSearchParams(next, { replace: true });
  };
  const openLot = (mtnId) => {
    const next = new URLSearchParams(searchParams);
    next.set("mtn", String(mtnId));
    setSearchParams(next);
  };
  const lotPanel = mtn ? <LotPanel key={mtn} mtnId={mtn} onClose={closeLot} /> : null;

  if (id) {
    return (
      <>
        <MaterialDetail key={id} materialId={id} customers={customers} onAll={() => setSearchParams({})} onOpenLot={openLot} />
        {lotPanel}
      </>
    );
  }
  return (
    <div className="jq-screen" data-testid="phone-materials">
      <div className="d-flex gap-2 mb-2" role="group" aria-label="Show">
        <button type="button" className={`btn btn-sm ${lotsTab ? "btn-outline-secondary" : "btn-secondary"}`} aria-pressed={!lotsTab} onClick={() => setSearchParams({})}>
          Materials
        </button>
        <button type="button" className={`btn btn-sm ${lotsTab ? "btn-secondary" : "btn-outline-secondary"}`} aria-pressed={lotsTab} onClick={() => setSearchParams({ tab: "tracking" })}>
          Lots (TRK)
        </button>
      </div>
      {lotsTab
        ? <LotLookup state={lotState} setState={setLotState} onOpen={(mtnId) => openLot(mtnId)} />
        : <MaterialList state={listState} setState={setListState} customers={customers} onOpen={(m) => setSearchParams({ id: String(m.material_id) })} />}
      {lotPanel}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The list: sheet / shape, search, "In stock only" (as the desktop: REM- remnant records hidden).

function MaterialList({ state, setState, customers, onOpen }) {
  const { category, search, inStockOnly } = state;
  const [loading, setLoading] = useState(state.materials === null);
  const [error, setError] = useState(null);
  const set = (patch) => setState((prev) => ({ ...prev, ...patch }));
  const setCategory = (category) => set({ category });
  const setSearch = (search) => set({ search });
  const setInStockOnly = (inStockOnly) => set({ inStockOnly });

  useEffect(() => {
    if (state.materials !== null) return undefined;
    let live = true;
    loadMaterialList()
      .then((rows) => { if (live) setState((prev) => ({ ...prev, materials: Array.isArray(rows) ? rows : [] })); })
      .catch((err) => { if (live) setError(err.message || "Couldn't load the materials."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [state.materials, setState]);

  const candidates = useMemo(() => materialListCandidates(state.materials || [], { category, search }), [state.materials, category, search]);
  const shown = inStockOnly ? candidates.filter(isInStock) : candidates;
  const hidden = candidates.length - shown.length;

  return (
    <>
      <div className="d-flex gap-2 mb-2 align-items-center" role="group" aria-label="Stock">
        {["Sheet Stock", "Shape Stock"].map((c) => (
          <button key={c} type="button" className={`btn btn-sm ${category === c ? "btn-primary" : "btn-outline-primary"}`} aria-pressed={category === c} onClick={() => setCategory(c)}>
            {c === "Sheet Stock" ? "Sheet" : "Shape"}
          </button>
        ))}
        <label className="ms-auto small d-flex align-items-center gap-1">
          <input type="checkbox" checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)} />
          In stock only
        </label>
      </div>
      <CardList
        items={shown}
        getKey={(m) => m.material_id}
        onItemClick={onOpen}
        search={{ value: search, onChange: setSearch, placeholder: "Search code or description" }}
        renderCard={(m) => {
          const f = materialFigures(m);
          return (
            <div data-testid={`material-card-${m.material_id}`}>
              <div className="fw-bold">{m.material_code}</div>
              <div className="jq-muted">{m.description}</div>
              <div className="jq-muted">
                {ownerName(customers, m.owner_customer_id)} · On hand <span className={f.onHand < 0 ? "text-danger fw-bold" : ""}>{f.onHand}</span>
                {" · "}Available <span className={f.available < 0 ? "text-danger fw-bold" : ""}>{f.available}</span>
                {f.inbound ? ` · +${f.inbound} coming` : ""}
              </div>
            </div>
          );
        }}
        loading={loading}
        error={error}
        empty={inStockOnly && hidden ? `Nothing in stock matches. Uncheck "In stock only" to see the ${hidden} out-of-stock match${hidden === 1 ? "" : "es"}.` : "No materials match."}
        testId="material-list"
      />
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// One material: figures, inbound, lots, transactions, at vendors; the three stock actions.

function MaterialDetail({ materialId, customers, onAll, onOpenLot }) {
  const [material, setMaterial] = useState(null);
  const [error, setError] = useState(null);
  const [lots, setLots] = useState(undefined); // undefined = loading; null = unreadable
  const [inbound, setInbound] = useState(undefined);
  const [transactions, setTransactions] = useState(undefined);
  const [days, setDays] = useState(7);
  const [custody, setCustody] = useState(undefined);
  const [allLots, setAllLots] = useState(false);
  const [panel, setPanel] = useState(null); // 'adjust' | 'transfer'
  const [defectBusy, setDefectBusy] = useState(false);
  const [labelUrl, setLabelUrl] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const live = useRef(true);
  const { palletBeacons } = useFeatures();
  const lotBeacons = useBeaconsByLot(palletBeacons.enabled);

  const readMaterial = useCallback(async () => {
    try {
      const m = await loadMaterial(materialId);
      if (live.current) setMaterial(m);
    } catch (err) {
      if (live.current) setError(err.message || "Couldn't reach JobQ.");
    }
  }, [materialId]);
  const readLots = useCallback(async () => {
    try {
      const rows = await loadMaterialLots(materialId);
      if (live.current) setLots(rows);
    } catch (_) {
      if (live.current) setLots(null);
    }
  }, [materialId]);
  const txSeq = useRef(0);
  const readTransactions = useCallback(async (n) => {
    const mine = ++txSeq.current; // a slower answer for another range is dropped
    setTransactions(undefined);
    try {
      const rows = await loadMaterialTransactions(materialId, lastDays(n));
      if (live.current && mine === txSeq.current) setTransactions(Array.isArray(rows) ? rows : []);
    } catch (err) {
      if (live.current && mine === txSeq.current) setTransactions(null);
    }
  }, [materialId]);

  useEffect(() => {
    live.current = true;
    readMaterial();
    readLots();
    loadInboundOrders(materialId).then((rows) => { if (live.current) setInbound(rows); });
    loadCustody(materialId).then((rows) => { if (live.current) setCustody(Array.isArray(rows) ? rows : []); }).catch(() => { if (live.current) setCustody(null); });
    return () => { live.current = false; };
  }, [materialId, readMaterial, readLots]);
  useEffect(() => { readTransactions(days); }, [days, readTransactions]);

  const afterChange = () => { readMaterial(); readLots(); readTransactions(days); };

  const back = <button type="button" className="btn btn-link px-0 mb-2" onClick={onAll}><i className="fas fa-chevron-left me-1" aria-hidden="true" />All materials</button>;
  if (error && !material) return <div className="jq-screen" data-testid="phone-material">{back}<div className="jq-note jq-note-danger" role="alert">{error}</div></div>;
  if (!material) return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;

  const f = materialFigures(material);
  const openLots = (lots || []).filter(isTransferableMtn);
  const shownLots = allLots ? (lots || []) : openLots;
  const openCustody = openCustodyRows(custody);

  const recordDefect = async () => {
    setLabelUrl(null);
    setDefectBusy(true);
    try {
      const r = await recordMaterialDefect({ materialId, ui: phoneUi });
      if (r.defectId) afterChange();
      if (r.labelUrl) setLabelUrl({ url: r.labelUrl, defectId: r.defectId });
    } finally {
      setDefectBusy(false);
    }
  };

  return (
    <div className="jq-screen" data-testid="phone-material">
      {back}
      <section className="jq-card" data-testid="material-header">
        <div className="fw-bold fs-5">{material.material_code}</div>
        <div>{material.description}</div>
        <div className="jq-muted">
          {ownerName(customers, material.owner_customer_id)}
          {material.material_type ? ` · ${material.material_type}` : ""}
          {material.surface_finish ? ` · ${material.surface_finish}` : ""}
        </div>
      </section>

      <dl className="jq-kv" data-testid="material-figures">
        <div><dt>On hand</dt><dd className={f.onHand < 0 ? "text-danger fw-bold" : ""}>{f.onHand}</dd></div>
        <div><dt>Reserved</dt><dd>{f.reserved}</dd></div>
        <div><dt>Available</dt><dd className={f.available < 0 ? "text-danger fw-bold" : ""}>{f.available}</dd></div>
        <div><dt>Coming in</dt><dd>{f.inbound}</dd></div>
        <div><dt>Total available</dt><dd className={f.totalAvailable < 0 ? "text-danger fw-bold" : ""}>{f.totalAvailable}</dd></div>
        {f.minimum > 0 && <div><dt>Minimum</dt><dd>{f.minimum}</dd></div>}
      </dl>

      <div className="d-flex flex-column gap-2 mb-3">
        <button type="button" className="btn btn-outline-primary btn-lg" onClick={() => setPanel("adjust")}>Adjust quantity</button>
        <button type="button" className="btn btn-outline-danger btn-lg" disabled={defectBusy} onClick={recordDefect}>{defectBusy ? "Recording…" : "Record defective sheets"}</button>
        <button type="button" className="btn btn-outline-primary btn-lg" onClick={() => { setReceiptUrl(null); setPanel("transfer"); }}>Transfer stock</button>
      </div>
      {labelUrl && (
        <div className="mb-3" data-testid="defect-label">
          <PdfButton url={labelUrl.url} filename={`Defect_${labelUrl.defectId}_label.pdf`} label={`Sheet label for defect #${labelUrl.defectId}`} />
        </div>
      )}
      {receiptUrl && (
        <div className="mb-3" data-testid="transfer-receipt">
          <PdfButton url={receiptUrl} filename="Transfer_receipt.pdf" label="Transfer receipt" />
        </div>
      )}

      <h2 className="jq-section-title">Coming in</h2>
      <div className="mb-3" data-testid="material-inbound">
        {inbound === undefined && <div className="jq-muted">Loading…</div>}
        {inbound === null && <div className="jq-muted">Couldn't read the purchase orders.</div>}
        {inbound && inbound.length === 0 && <div className="jq-muted">No inbound orders scheduled.</div>}
        {(inbound || []).map((o) => (
          <div key={`${o.purchase_order_id}-${o.po_number}`} className="jq-task-line">
            <div className="jq-task-line-main">
              <div>PO {o.po_number || o.purchase_order_id}{o.supplier ? ` · ${o.supplier}` : ""}</div>
              <div className="jq-muted">Expected {inboundDateLabel(o.estimated_receive_date)}</div>
            </div>
            <span className="fw-bold">{o.quantity_due}</span>
          </div>
        ))}
      </div>

      <div className="d-flex align-items-center">
        <h2 className="jq-section-title flex-grow-1">Lots (TRK)</h2>
        {lots && lots.length > openLots.length && (
          <button type="button" className="btn btn-link btn-sm" onClick={() => setAllLots((v) => !v)}>{allLots ? "Open lots only" : `All ${lots.length}`}</button>
        )}
      </div>
      <div className="mb-3" data-testid="material-lots">
        {lots === undefined && <div className="jq-muted">Loading…</div>}
        {lots === null && <div className="jq-muted">Couldn't read the lots.</div>}
        {lots && shownLots.length === 0 && <div className="jq-muted">{lots.length ? "No open lots." : "No lots on this material."}</div>}
        {shownLots.map((l) => {
          const b = lotBeacons.byLot[l.mtn_id] || null;
          const findHere = b ? lotBeacons.finder.finds[b.beacon_id] : null;
          const findBusy = !!b && (lotBeacons.finding === b.beacon_id || lotBeacons.finder.busy(b.beacon_id));
          const lotTrk = l.tracking_number_display || trk(l.mtn_id);
          return (
            <div key={l.mtn_id} className="jq-task-line mb-1" style={{ flexWrap: "wrap" }} data-testid={`material-lot-${l.mtn_id}`}>
              <button type="button" className="jq-task-line-main d-flex align-items-center gap-2 text-start btn btn-link text-reset text-decoration-none p-0" style={{ flex: "1 1 0", minWidth: 0 }} onClick={() => onOpenLot(l.mtn_id)}>
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div>TRK {lotTrk}{l.heat_number ? ` · heat ${l.heat_number}` : ""}</div>
                  <div className="jq-muted">{[l.po_number ? `PO ${l.po_number}` : null, l.supplier || l.customer_name, lotStatusLabel(l.lot_status || l.status), b ? b.beacon_id : null].filter(Boolean).join(" · ")}</div>
                </div>
                <span className="fw-bold">{mtnRemaining(l)} / {l.quantity}</span>
              </button>
              {b && (
                <button
                  type="button"
                  className="btn btn-success"
                  style={{ minHeight: 44 }}
                  disabled={findBusy}
                  aria-label={`Find ${b.beacon_id} on TRK ${lotTrk}`}
                  onClick={() => lotBeacons.find(b)}
                >
                  <i className="fas fa-lightbulb me-1" aria-hidden="true" />{findBusy ? "…" : "Find"}
                </button>
              )}
              {findHere && (
                <FindProgress find={findHere} onRetry={() => lotBeacons.find(b)} onChange={lotBeacons.finder.track(b.beacon_id)}
                  onSettled={lotBeacons.reload} className="w-100" />
              )}
            </div>
          );
        })}
        {lotBeacons.msg && (
          <div className={`jq-note ${lotBeacons.msg.tone === "danger" ? "jq-note-danger" : "jq-note-info"} mt-1`} role="status">{lotBeacons.msg.text}</div>
        )}
      </div>

      <div className="d-flex align-items-center">
        <h2 className="jq-section-title flex-grow-1">Transactions, last {days} days</h2>
        {days === 7 && <button type="button" className="btn btn-link btn-sm" onClick={() => setDays(30)}>Last 30 days</button>}
      </div>
      <div className="mb-3" data-testid="material-transactions">
        {transactions === undefined && <div className="jq-muted">Loading…</div>}
        {transactions === null && <div className="jq-muted">Couldn't read the transactions.</div>}
        {transactions && transactions.length === 0 && <div className="jq-muted">No transactions in that time.</div>}
        {(transactions || []).map((t, i) => (
          <div key={t.transaction_id || i} className="jq-task-line">
            <div className="jq-task-line-main">
              <div>{t.reason}</div>
              <div className="jq-muted">
                {stamp(t.created_at)} · {t.user_name || "JobQ"}
                {t.job_master_num ? ` · Job# ${t.job_master_num}` : ""}
                {t.po_number ? ` · PO# ${t.po_number}` : ""}
              </div>
            </div>
            <span className={`fw-bold ${Number(t.delta) < 0 ? "text-danger" : "text-success"}`}>{Number(t.delta) > 0 ? `+${t.delta}` : t.delta}</span>
          </div>
        ))}
      </div>

      <h2 className="jq-section-title">At vendors</h2>
      <div className="mb-3" data-testid="material-custody">
        {custody === undefined && <div className="jq-muted">Loading…</div>}
        {custody === null && <div className="jq-muted">Couldn't read what is at vendors.</div>}
        {custody && openCustody.length === 0 && <div className="jq-muted">None of this material is at a vendor.</div>}
        {openCustody.map((c) => (
          <div key={c.custody_id} className="jq-task-line">
            <div className="jq-task-line-main">
              <div>{c.vendor_name}{c.po_number ? ` · PO ${c.po_number}` : ""}</div>
              <div className="jq-muted">
                {[c.still_to_use != null ? `${c.still_to_use} still to use` : null, Number(c.expected_back) > 0 ? `${c.expected_back} coming back` : null].filter(Boolean).join(" · ")}
              </div>
            </div>
            <span className="fw-bold">{c.quantity}</span>
          </div>
        ))}
      </div>

      <AdjustPanel open={panel === "adjust"} material={material} onClose={() => setPanel(null)} onDone={() => { setPanel(null); afterChange(); }} />
      {panel === "transfer" && (
        <TransferPanel
          material={material}
          lots={lots || []}
          customers={customers}
          onClose={() => setPanel(null)}
          onNeedLot={readLots}
          onDone={(url) => { setPanel(null); if (url) setReceiptUrl(url); afterChange(); }}
        />
      )}
    </div>
  );
}

// Adjust quantity = a stock transaction (a CHANGE, not a new total): add or take out, why, an optional job.
function AdjustPanel({ open, material, onClose, onDone }) {
  const [direction, setDirection] = useState(-1);
  const [count, setCount] = useState("");
  const [reason, setReason] = useState("");
  const [jobId, setJobId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setCount(""); setReason(""); setJobId(""); setDirection(-1); }
  }, [open]);

  // The change as typed digits (never a number turned back into a string: 1e21 would read as 1).
  const digits = count.replace(/^0+/, "");
  const delta = digits ? `${direction < 0 ? "-" : ""}${digits}` : "";
  const save = async () => {
    setBusy(true);
    try {
      const r = await addMaterialTransaction({ materialId: material.material_id, delta, reason, jobId, ui: phoneUi });
      if (r.done) onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <FullScreenPanel open={open} title="Adjust quantity" onClose={onClose} testId="material-adjust">
      <div className="jq-muted mb-2">{material.material_code} · on hand {materialFigures(material).onHand}</div>
      <div className="d-flex gap-2 mb-2" role="group" aria-label="Direction">
        <button type="button" className={`btn flex-grow-1 ${direction < 0 ? "btn-danger" : "btn-outline-danger"}`} aria-pressed={direction < 0} onClick={() => setDirection(-1)}>Take out</button>
        <button type="button" className={`btn flex-grow-1 ${direction > 0 ? "btn-success" : "btn-outline-success"}`} aria-pressed={direction > 0} onClick={() => setDirection(1)}>Add</button>
      </div>
      <div className="jq-filter-field">
        <label htmlFor="material-adjust-count">How many</label>
        <input id="material-adjust-count" type="text" inputMode="numeric" pattern="[0-9]*" className="form-control form-control-lg" value={count} maxLength={6} onChange={(e) => setCount(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} />
      </div>
      <div className="jq-filter-field">
        <label htmlFor="material-adjust-reason">Reason</label>
        <select id="material-adjust-reason" className="form-select form-select-lg" value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">-- Select reason --</option>
          {TRANSACTION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div className="jq-filter-field">
        <label htmlFor="material-adjust-job">Job ID (optional; the job's id, not its Job #)</label>
        <input id="material-adjust-job" type="text" inputMode="numeric" pattern="[0-9]*" className="form-control form-control-lg" value={jobId} onChange={(e) => setJobId(e.target.value.replace(/[^0-9]/g, ""))} />
      </div>
      {delta && <div className="jq-muted mb-2">Records {direction > 0 ? `+${delta}` : delta}: on hand becomes {materialFigures(material).onHand + Number(delta)}.</div>}
      <button type="button" className="btn btn-primary btn-lg w-100" disabled={busy || !delta || !reason} onClick={save}>{busy ? "Saving…" : "Save"}</button>
    </FullScreenPanel>
  );
}

// Transfer stock to another material record (the same steel under another owner), from a lot.
function TransferPanel({ material, lots, customers, onClose, onNeedLot, onDone }) {
  const transferable = lots.filter(isTransferableMtn);
  const [sourceMtnId, setSourceMtnId] = useState(onlyOpenLotId(lots));
  const [destId, setDestId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [everyMaterial, setEveryMaterial] = useState(false);
  const [search, setSearch] = useState("");
  const [materials, setMaterials] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  useEffect(() => {
    let live = true;
    loadMaterialList().then((rows) => { if (live) setMaterials(rows); }).catch(() => { if (live) setMaterials([]); });
    return () => { live = false; };
  }, []);

  const filters = everyMaterial ? { search } : { ...transferFilterDefaults(material), search };
  const destinations = transferDestinations(materials || [], material.material_id, filters);
  const dest = destinations.find((m) => String(m.material_id) === String(destId)) || null;
  // A destination that the search or the filter no longer shows is no longer chosen (it would still be sent).
  useEffect(() => {
    if (destId && materials && !dest) setDestId("");
  }, [destId, dest, materials]);
  const pickedLot = transferable.find((l) => String(l.mtn_id) === String(sourceMtnId)) || null;

  const confirm = async () => {
    if (busy) return;
    setNote(null);
    setBusy(true); // before the PIN: a second tap can't open a second PIN sheet
    try {
      const r = await transferStock({
        sourceMaterialId: material.material_id, destinationMaterialId: destId, quantity, reason, sourceMtnId,
        onHand: material.stock_quantity, lots, ui: phoneUi,
      });
      if (r.needsLot) { setNote(r.message); onNeedLot(); return; }
      if (r.cancelled) { setNote(r.message); return; }   // said No to moving earmarked sheets
      if (r.done) onDone(r.receiptUrl || null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FullScreenPanel open title="Transfer stock" onClose={busy ? undefined : onClose} testId="material-transfer">
      <div className="jq-muted mb-2">From {material.material_code} · on hand {materialFigures(material).onHand}</div>
      {note && <div className="jq-note jq-note-warn" role="alert">{note}</div>}
      {lots.length > 0 && (
        <div className="jq-filter-field">
          <label htmlFor="transfer-source">Source lot (TRK)</label>
          <select id="transfer-source" className="form-select form-select-lg" value={sourceMtnId} onChange={(e) => setSourceMtnId(e.target.value)}>
            <option value="">-- Select the stack you're drawing from --</option>
            {transferable.map((l) => (
              <option key={l.mtn_id} value={l.mtn_id}>TRK {l.tracking_number_display || trk(l.mtn_id)} · {mtnRemaining(l)} left{l.heat_number ? ` · heat ${l.heat_number}` : ""}</option>
            ))}
          </select>
        </div>
      )}
      <div className="jq-filter-field">
        <label htmlFor="transfer-dest">Destination material</label>
        <input type="search" aria-label="Search the destinations" className="form-control mb-1" placeholder="Search code or description" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select id="transfer-dest" className="form-select form-select-lg" value={destId} onChange={(e) => setDestId(e.target.value)}>
          <option value="">{materials === null ? "Loading…" : `-- Select (${destinations.length}) --`}</option>
          {dest && destinations.indexOf(dest) >= 200 && <option value={dest.material_id}>{dest.material_code} | {ownerName(customers, dest.owner_customer_id)} (QoH: {dest.stock_quantity})</option>}
          {destinations.slice(0, 200).map((m) => (
            <option key={m.material_id} value={m.material_id}>{m.material_code} | {ownerName(customers, m.owner_customer_id)} (QoH: {m.stock_quantity})</option>
          ))}
        </select>
        {destinations.length > 200 && <div className="jq-muted small">Showing 200 of {destinations.length}: search to narrow them.</div>}
        <label className="small d-flex align-items-center gap-1 mt-1">
          <input type="checkbox" checked={everyMaterial} onChange={(e) => setEveryMaterial(e.target.checked)} />
          Show every material (not just the same type, thickness and finish)
        </label>
      </div>
      <div className="jq-filter-field">
        <label htmlFor="transfer-qty">Quantity to transfer</label>
        <input id="transfer-qty" type="text" inputMode="numeric" pattern="[0-9]*" className="form-control form-control-lg" value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/[^0-9]/g, ""))} />
      </div>
      <div className="jq-filter-field">
        <label htmlFor="transfer-reason">Reason</label>
        <input id="transfer-reason" type="text" className="form-control form-control-lg" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Job #1234 usage" />
      </div>
      {dest && quantity && (
        <div className="jq-note jq-note-info" data-testid="transfer-summary">
          {quantity} from {pickedLot ? `TRK ${pickedLot.tracking_number_display || trk(pickedLot.mtn_id)}` : material.material_code} → {dest.material_code} ({ownerName(customers, dest.owner_customer_id)})
        </div>
      )}
      <button type="button" className="btn btn-primary btn-lg w-100" disabled={busy} onClick={confirm}>{busy ? "Transferring…" : "Confirm transfer"}</button>
    </FullScreenPanel>
  );
}

// ---------------------------------------------------------------------------------------------
// Lot lookup: status (as the desktop's Material Tracking tab), search by TRK / material / heat / PO /
// supplier / customer.

function LotLookup({ state, setState, onOpen }) {
  const { status, search } = state;
  const setStatus = (status) => setState((prev) => ({ ...prev, status }));
  const setSearch = (search) => setState((prev) => ({ ...prev, search }));
  const [rows, setRows] = useState(undefined);
  const [limit, setLimit] = useState(LOT_PAGE);
  const { palletBeacons } = useFeatures();
  const { byLot } = useBeaconsByLot(palletBeacons.enabled); // the beacon's ID on its lot's card; Find is in the lot

  useEffect(() => {
    let live = true;
    setRows(undefined);
    loadTrackingLots({ status }).then((r) => { if (live) setRows(r); });
    return () => { live = false; };
  }, [status]);

  const matches = useMemo(() => [...filterLots(rows || [], { search })].sort((a, b) => (b.mtn_id || 0) - (a.mtn_id || 0)), [rows, search]);
  const shown = matches.slice(0, limit);
  // A typed or pasted lot number (000409, TRK 409, a /s/mtn- link) opens that lot even when it is older than
  // the newest rows the list holds.
  const typedLot = parseLotNumber(search);
  const truncated = Array.isArray(rows) && rows.length >= TRACKING_LIMIT;

  return (
    <>
      <div className="jq-filter-field">
        <label htmlFor="lot-status">Status</label>
        <select id="lot-status" className="form-select" value={status} onChange={(e) => { setStatus(e.target.value); setLimit(LOT_PAGE); }}>
          {TRACKING_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {typedLot && (
        <button type="button" className="btn btn-outline-primary w-100 mb-2" onClick={() => onOpen(typedLot)}>Open TRK {trk(typedLot)}</button>
      )}
      {truncated && <div className="jq-muted small mb-1">Showing the newest {TRACKING_LIMIT} lots: an older one opens by its TRK number.</div>}
      <CardList
        items={shown}
        getKey={(l) => l.mtn_id}
        onItemClick={(l) => onOpen(l.mtn_id)}
        search={{ value: search, onChange: (v) => { setSearch(v); setLimit(LOT_PAGE); }, placeholder: "TRK#, material, heat #, PO, supplier…" }}
        renderCard={(l) => (
          <div data-testid={`lot-card-${l.mtn_id}`}>
            <div className="d-flex gap-2 align-items-center">
              <span className="fw-bold">TRK {l.tracking_number_display || trk(l.mtn_id)}</span>
              <span className="jq-tag">{lotStatusLabel(l.lot_status || l.status)}</span>
              {byLot[l.mtn_id] && <span className="jq-tag jq-tag-next font-monospace" data-testid={`lot-card-beacon-${l.mtn_id}`}>{byLot[l.mtn_id].beacon_id}</span>}
              <span className="ms-auto fw-bold">{mtnRemaining(l)} / {l.quantity}</span>
            </div>
            <div className="jq-muted">{l.material_code || l.material_description}</div>
            <div className="jq-muted">{[l.heat_number ? `heat ${l.heat_number}` : null, l.po_number ? `PO ${l.po_number}` : null, l.supplier || l.customer_name].filter(Boolean).join(" · ")}</div>
          </div>
        )}
        loading={rows === undefined}
        error={rows === null ? "Couldn't read the lots." : null}
        empty="No lots match."
        testId="lot-list"
      />
      {matches.length > shown.length && (
        <button type="button" className="btn btn-outline-secondary jq-show-more w-100 mt-2" onClick={() => setLimit((n) => n + LOT_PAGE)}>
          Show more ({matches.length - shown.length} left)
        </button>
      )}
    </>
  );
}

// One lot: its beacon with Find (LotBeaconCard), then read only: what it is, where it came from, how much
// is left, what it was cut into, its notes. Changes to a lot (counts, notes, earmarks, putting a beacon on
// or taking it off) are made on the desktop TRK popup.
function LotPanel({ mtnId, onClose }) {
  const [lot, setLot] = useState(null);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(undefined);
  const [notes, setNotes] = useState(undefined);

  useEffect(() => {
    let live = true;
    loadLotDetail(mtnId)
      .then((d) => { if (live) setLot(d); })
      .catch((err) => { if (live) setError(String(err || "") || "Couldn't load this lot."); });
    loadLotUsage(mtnId).then((u) => { if (live) setUsage(u); }).catch(() => { if (live) setUsage(null); });
    loadLotNotes(mtnId).then((n) => { if (live) setNotes(n); });
    return () => { live = false; };
  }, [mtnId]);

  const title = `TRK ${lot && lot.tracking_number_display ? lot.tracking_number_display : trk(mtnId)}`;
  const { sheets, remnantCuts } = splitLotUsage(usage || []);
  const openNotes = (notes || []).filter((n) => !n.resolved_at);

  return (
    <FullScreenPanel open title={title} onClose={onClose} testId="phone-material-lot">
      {error && <div className="jq-note jq-note-danger" role="alert">{error}</div>}
      {!lot && !error && <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /></div>}
      {lot && (
        <>
          <div className="fw-semibold">{lot.material_description || lot.material_code}</div>
          {lot.material_code && lot.material_description && <div className="jq-muted">{lot.material_code}</div>}
          {lot.earmark_text && <div className="jq-note jq-note-info mt-2">{lot.earmark_text}</div>}
        </>
      )}
      {/* the beacon and its Find read on their own: a scanned TRK can Find before (or without) the lot's details */}
      <LotBeaconCard mtnId={mtnId} />
      {lot && (
        <>
          <dl className="jq-kv">
            <div><dt>Left</dt><dd>{mtnRemaining(lot)}</dd></div>
            <div><dt>Received</dt><dd>{lot.quantity ?? "?"}</dd></div>
            <div><dt>Status</dt><dd>{lotStatusLabel(lot.lot_status)}</dd></div>
            <div><dt>Heat</dt><dd>{lot.heat_number || "None"}</dd></div>
            {lot.po_number && <div><dt>PO</dt><dd>{lot.po_number}</dd></div>}
            {(lot.supplier || lot.customer_name) && <div><dt>From</dt><dd>{lot.supplier || lot.customer_name}</dd></div>}
            {lot.parent_mtn_id && <div><dt>Cut from</dt><dd>TRK {trk(lot.parent_mtn_id)}</dd></div>}
            {lot.created_at && <div><dt>Lot started</dt><dd>{stamp(lot.created_at)}</dd></div>}
          </dl>

          <h2 className="jq-section-title mt-3">
            Used: {sheets} sheet{sheets === 1 ? "" : "s"}{remnantCuts ? ` + ${remnantCuts} remnant cut${remnantCuts === 1 ? "" : "s"}` : ""}
          </h2>
          <div data-testid="lot-usage">
            {usage === undefined && <div className="jq-muted">Loading…</div>}
            {usage === null && <div className="jq-muted">Couldn't read what this lot was used for.</div>}
            {usage && usage.length === 0 && <div className="jq-muted">No sheets recorded against this tracking number yet.</div>}
            {(usage || []).map((u, i) => (
              <div key={`${u.task_id}_${i}`} className="jq-task-line">
                <div className="jq-task-line-main">
                  <div>
                    {u.master_job_num ? `Job ${u.master_job_num}` : (u.job_id ? `Job ${u.job_id}` : "—")}
                    {u.workstation ? ` · ${u.workstation}` : ""}
                    {u.is_remnant_cut && <span className="jq-tag ms-2">remnant</span>}
                  </div>
                  <div className="jq-muted">{[u.cut_sheet_name, u.recorded_by_name, u.recorded_at ? stamp(u.recorded_at) : null].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
            ))}
          </div>

          <h2 className="jq-section-title mt-3">Notes</h2>
          <div data-testid="lot-notes">
            {notes === undefined && <div className="jq-muted">Loading…</div>}
            {notes && notes.length === 0 && <div className="jq-muted">No notes on this tracking number.</div>}
            {[...openNotes, ...(notes || []).filter((n) => n.resolved_at)].map((n) => (
              <div key={n.note_id} className={`jq-note ${n.resolved_at ? "" : n.severity === "critical" ? "jq-note-danger" : "jq-note-warn"} mb-1`}>
                <div style={{ whiteSpace: "pre-wrap" }}>{n.note_text}</div>
                <div className="jq-muted small">
                  {[n.severity, n.resolved_at ? "resolved" : null, n.created_by_name, n.created_at ? stamp(n.created_at) : null].filter(Boolean).join(" · ")}
                </div>
              </div>
            ))}
          </div>
          <div className="jq-muted mt-3">Counts, notes, earmarks and which beacon is on the pallet are changed on the desktop TRK popup.</div>
        </>
      )}
    </FullScreenPanel>
  );
}
